/**
 * OutcomeMeasuresPanel — trend charts + quick logging for a client.
 *
 * Renders one card per measure that has recorded values, showing a
 * mini sparkline + latest value/delta. Empty state offers a "Log
 * first measurement" CTA. A single dialog handles adding new values,
 * with the measure dropdown driven by the seeded registry.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { LineChart, Plus, Trash2, ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useCanonicalClient } from "@/lib/canonicalClient";

export default function OutcomeMeasuresPanel({ email, refreshKey }) {
  const canonical = useCanonicalClient();
  const [registry, setRegistry] = useState([]);
  const [byMeasure, setByMeasure] = useState({});
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const measureParams = { grouped: true };
      // Phase-2: narrow to just this canonical client's measurements
      // when the operator is inside `?as=<cid>` scoped mode.
      if (canonical?.clientId) measureParams.canonical_client_id = canonical.clientId;
      const [reg, vals] = await Promise.all([
        api.get("/outcome-measures"),
        api.get(`/health-record/${encodeURIComponent(email)}/measurements`, { params: measureParams }),
      ]);
      setRegistry(reg.data?.measures || []);
      setByMeasure(vals.data?.by_measure || {});
    } catch (e) {
      toast.error("Could not load outcome measures", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, canonical?.clientId]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const measured = useMemo(() => Object.values(byMeasure), [byMeasure]);

  async function removeMeasurement(id) {
    if (!window.confirm("Delete this measurement value?")) return;
    try {
      await api.delete(`/health-record/measurements/${id}`);
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-3" data-testid="outcome-measures-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <LineChart className="h-3 w-3" /> Outcome measures
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowLog(true)} data-testid="om-log-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Log measurement
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 p-3 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : measured.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          No measurements recorded yet. Start with a baseline (e.g. 30-Second Sit-to-Stand, TUG or VAS) so
          progress can be trended over time.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {measured.map((m) => (
            <MeasureCard key={m.measure_id} m={m} onRemove={removeMeasurement} />
          ))}
        </div>
      )}

      {showLog && (
        <LogMeasurementDialog
          email={email}
          registry={registry}
          onClose={() => setShowLog(false)}
          onSaved={() => { setShowLog(false); refresh(); }}
        />
      )}
    </div>
  );
}


function MeasureCard({ m, onRemove }) {
  const points = m.points || [];
  const last = points[points.length - 1];
  const prev = points.length >= 2 ? points[points.length - 2] : null;
  const delta = last && prev ? last.value - prev.value : 0;
  // Direction awareness: for "lower is better" measures a negative
  // delta is progress and gets the green tone.
  const isProgress = m.direction === "higher" ? delta > 0 : delta < 0;
  const isRegress  = m.direction === "higher" ? delta < 0 : delta > 0;

  return (
    <Card className="p-3 rounded-lg border border-zinc-200 shadow-none" data-testid={`om-card-${m.measure_code}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-zinc-500 truncate">{m.measure_name}</div>
          <div className="text-lg font-semibold text-zinc-950 font-mono-num tabular-nums">
            {last ? `${last.value} ${m.unit || ""}` : "—"}
          </div>
        </div>
        {prev && (
          <div className={cn(
            "text-2xs rounded-full px-1.5 py-0.5 border inline-flex items-center gap-1 font-mono-num",
            isProgress ? "text-emerald-700 border-emerald-200 bg-emerald-50"
            : isRegress ? "text-red-700 border-red-200 bg-red-50"
            : "text-zinc-500 border-zinc-200 bg-zinc-50",
          )}>
            {isProgress ? <ArrowUpRight className="h-3 w-3" /> : isRegress ? <ArrowDownRight className="h-3 w-3" /> : null}
            {delta > 0 ? "+" : ""}{delta.toFixed(1)}
          </div>
        )}
      </div>
      <Sparkline points={points} direction={m.direction} />
      <div className="mt-2 flex items-center justify-between text-2xs text-zinc-500">
        <span>{points.length} data point{points.length === 1 ? "" : "s"}</span>
        {last && (
          <button
            onClick={() => onRemove(last.id)}
            className="text-red-600 hover:bg-red-50 rounded p-1"
            title="Delete last measurement"
            data-testid={`om-remove-${m.measure_code}`}
          ><Trash2 className="h-3 w-3" /></button>
        )}
      </div>
    </Card>
  );
}


function Sparkline({ points, direction }) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) {
    return (
      <div className="mt-2 h-10 flex items-center text-2xs text-zinc-400 italic">
        One data point — trend appears with a second measurement.
      </div>
    );
  }
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const w = 200, h = 40;
  const span = max === min ? 1 : max - min;
  const path = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p.value - min) / span) * (h - 6) - 3;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  const last = values[values.length - 1];
  const first = values[0];
  const positive = direction === "higher" ? last >= first : last <= first;
  const stroke = positive ? "#059669" : "#dc2626";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-2 w-full h-10" preserveAspectRatio="none">
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.4} />
      {points.map((p, i) => {
        const x = (i / (points.length - 1)) * w;
        const y = h - ((p.value - min) / span) * (h - 6) - 3;
        return <circle key={i} cx={x} cy={y} r={1.6} fill={stroke} />;
      })}
    </svg>
  );
}


function LogMeasurementDialog({ email, registry, onClose, onSaved }) {
  const canonical = useCanonicalClient();
  const [measureId, setMeasureId] = useState(registry[0]?.id || "");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [measuredAt, setMeasuredAt] = useState("");
  const [saving, setSaving] = useState(false);

  const measure = registry.find((m) => m.id === measureId);

  async function save() {
    if (!measureId) { toast.error("Pick a measure"); return; }
    const v = parseFloat(value);
    if (Number.isNaN(v)) { toast.error("Enter a numeric value"); return; }
    setSaving(true);
    try {
      await api.post(`/health-record/${encodeURIComponent(email)}/measurements`, {
        measure_id: measureId,
        value: v,
        notes,
        measured_at: measuredAt ? new Date(measuredAt).toISOString() : undefined,
        // Phase-2 scoped writes — measurement lands on this canonical
        // client's list only (Judy vs Rosemarie).
        canonical_client_id: canonical?.clientId || null,
      });
      toast.success("Measurement recorded");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="om-log-dialog">
        <DialogHeader>
          <DialogTitle>Log outcome measurement</DialogTitle>
          <DialogDescription>
            Add a single data point. Trend graphs update automatically once two or more values are recorded.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Measure</Label>
            <select
              value={measureId} onChange={(e) => setMeasureId(e.target.value)}
              className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
              data-testid="om-log-measure"
            >
              {registry.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · {m.unit} ({m.direction === "higher" ? "higher = better" : "lower = better"})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Value ({measure?.unit || ""})</Label>
              <Input
                type="number" step="0.1"
                value={value} onChange={(e) => setValue(e.target.value)}
                min={measure?.min ?? undefined} max={measure?.max ?? undefined}
                data-testid="om-log-value"
              />
            </div>
            <div>
              <Label className="text-xs">Measured at (optional)</Label>
              <Input
                type="datetime-local"
                value={measuredAt} onChange={(e) => setMeasuredAt(e.target.value)}
                data-testid="om-log-measured-at"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="om-log-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="om-log-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
